import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

/**
 * Default scroll controller using native browser scrolling behavior.
 * Handles `scrollTop`, easing delta over time for smooth lerped animations.
 */
export class StringScrollDefault extends ScrollController {
  /** Unique name identifier for this scroll mode. */
  public readonly name: string = "default";

  /**
   * Constructs a new instance of the default scroll controller.
   * @param context Shared string system context.
   */
  constructor(context: StringContext) {
    super(context);
  }

  /**
   * Called every animation frame.
   * Applies easing to scroll delta and updates lerped value.
   * Fires `onScrollStop` once movement has settled.
   */
  public onFrame(): void {
    if (this.context.data.scroll.delta !== 0) {
      const delta = this.context.data.scroll.delta * this.context.data.scroll.speedAccelerate;
      this.context.data.scroll.delta -= delta;
      this.context.data.scroll.lerped = delta;

      if (Math.abs(this.context.data.scroll.lerped) < 0.1) {
        this.context.data.scroll.delta = 0;
        this.context.data.scroll.lerped = 0;
        this.onScrollStop();
      }
    }
  }

  /**
   * Called on native scroll event.
   * Syncs the internal scroll state with the current page scroll.
   * @param e Scroll event object (unused).
   */
  public onScroll(e: any): void {
    const scrollTop = this.context.data.scroll.elementContainer.scrollTop;
    this.context.data.scroll.current = scrollTop;
    this.context.data.scroll.target = scrollTop;
    this.context.data.scroll.transformedCurrent = scrollTop;
  }

  /**
   * Handles wheel input by updating scroll delta for easing.
   * Triggers `onScrollStart` if delta begins from zero.
   * @param e Wheel event.
   */
  public onWheel(e: any): void {
    if (e.deltaY !== 0) {
      if (this.context.data.scroll.delta === 0) {
        this.onScrollStart();
      }

      const plusDelta = e.deltaY;

      // Prevent negative delta from triggering at top of scroll
      if (this.context.data.scroll.target === 0) {
        this.context.data.scroll.delta += Math.max(0, e.deltaY);
      }

      this.context.data.scroll.delta += plusDelta;
    }
  }

  public scrollTo(position: number) {
    this.context.data.scroll.target = position;
    this.context.data.scroll.delta = 1;
  }
}
